import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Truck, Plus, Search, Edit, Trash2, Phone, Mail, MapPin } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SkeletonGrid, EmptyState } from '../../components/LoadingStates';
import { supplierService } from '../../services/inventory/supplier';
import { Supplier, CreateSupplierRequest } from '../../types/inventory';
import { Modal, Field, ModalActions, inputCls } from './StockItemsTab';

interface Props {
  org: string;
  restaurantId: string;
}

type SupplierFormData = Omit<CreateSupplierRequest, 'organizationCode' | 'restaurantId'>;

export const SuppliersTab: React.FC<Props> = ({ org, restaurantId }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; title: string; message: string; type: 'warning' | 'danger' | 'info'; confirmText: string; onConfirm: () => void }>({
    open: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: () => {},
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<SupplierFormData>();

  const { data: suppliers, isLoading } = useQuery(
    ['inventory-suppliers', org, restaurantId],
    () => supplierService.getSuppliers(org, restaurantId),
  );

  const createMutation = useMutation(supplierService.createSupplier, {
    onSuccess: () => { queryClient.invalidateQueries(['inventory-suppliers']); close(); toast.success('Supplier added'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to add supplier'); },
  });
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateSupplierRequest> }) => supplierService.updateSupplier(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries(['inventory-suppliers']); close(); toast.success('Supplier updated'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update supplier'); },
    },
  );
  const toggleMutation = useMutation(supplierService.toggleActive, {
    onSuccess: () => { queryClient.invalidateQueries(['inventory-suppliers']); toast.success('Status updated'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update status'); },
  });
  const deleteMutation = useMutation(supplierService.deleteSupplier, {
    onSuccess: () => { queryClient.invalidateQueries(['inventory-suppliers']); toast.success('Supplier deleted'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to delete supplier'); },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers ?? [];
    return (suppliers ?? []).filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.contactPerson?.toLowerCase().includes(q) ||
      s.phone?.includes(q),
    );
  }, [suppliers, search]);

  function close() { setIsFormOpen(false); setEditing(null); reset(); }
  function openCreate() { setEditing(null); reset({}); setIsFormOpen(true); }
  function openEdit(s: Supplier) {
    setEditing(s);
    reset({ name: s.name, contactPerson: s.contactPerson, phone: s.phone, email: s.email, address: s.address, gstNumber: s.gstNumber, paymentTerms: s.paymentTerms, notes: s.notes });
    setIsFormOpen(true);
  }

  const onSubmit = (data: SupplierFormData) => {
    const payload: CreateSupplierRequest = {
      ...data,
      name: data.name.trim(),
      organizationCode: org,
      restaurantId,
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  const askDelete = (s: Supplier) => setConfirm({
    open: true, title: 'Delete Supplier', message: `Delete "${s.name}"?`, type: 'danger', confirmText: 'Delete',
    onConfirm: () => { deleteMutation.mutate(s.id); setConfirm(c => ({ ...c, open: false })); },
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search suppliers..."
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-[var(--color-border-primary)] bg-[var(--color-background-secondary)] focus:outline-none focus:border-[var(--color-primary-400)] focus:ring-4 focus:ring-[var(--color-primary-100)]" />
        </div>
        <button onClick={openCreate} className="inline-flex items-center justify-center btn-primary">
          <Plus className="h-5 w-5 mr-2" /> Add Supplier
        </button>
      </div>

      {isLoading ? (
        <SkeletonGrid items={6} columns={3} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Truck className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No suppliers"
          description={search ? 'Try a different search.' : 'Add suppliers to record purchases against them.'}
          action={!search ? <button onClick={openCreate} className="inline-flex items-center btn-primary"><Plus className="h-5 w-5 mr-2" /> Add Supplier</button> : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => (
            <div key={s.id} className={clsx('rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-5 transition-all', !s.active && 'opacity-60')}
              style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: currentTheme.colors.primary[100] }}>
                    <Truck className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate" style={{ color: currentTheme.colors.text.primary }}>{s.name}</h3>
                    {s.contactPerson && <p className="text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>{s.contactPerson}</p>}
                  </div>
                </div>
                <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0', s.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700')}>
                  {s.active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-1.5 text-sm mb-4" style={{ color: currentTheme.colors.text.secondary }}>
                {s.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {s.phone}</p>}
                {s.email && <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{s.email}</span></p>}
                {s.address && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{s.address}</span></p>}
                {s.gstNumber && <p className="text-xs">GST: {s.gstNumber}</p>}
                {s.paymentTerms && <p className="text-xs">Terms: {s.paymentTerms}</p>}
              </div>

              <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                <button onClick={() => toggleMutation.mutate(s.id)} className="px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{ backgroundColor: s.active ? '#fef2f2' : '#f0fdf4', color: s.active ? '#dc2626' : '#16a34a' }}>
                  {s.active ? 'Deactivate' : 'Activate'}
                </button>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(s)} className="p-2 rounded-lg" style={{ color: currentTheme.colors.primary[600], backgroundColor: currentTheme.colors.primary[50] }}><Edit className="h-4 w-4" /></button>
                  <button onClick={() => askDelete(s)} className="p-2 rounded-lg" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFormOpen && (
        <Modal title={editing ? 'Edit Supplier' : 'Add Supplier'} onClose={close} wide>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Supplier Name" error={errors.name?.message} required>
                <input {...register('name', { required: 'Name is required' })} className={inputCls} placeholder="e.g., Fresh Farms Pvt Ltd" />
              </Field>
              <Field label="Contact Person"><input {...register('contactPerson')} className={inputCls} /></Field>
              <Field label="Phone"><input {...register('phone')} className={inputCls} /></Field>
              <Field label="Email"><input type="email" {...register('email')} className={inputCls} /></Field>
              <Field label="GST Number"><input {...register('gstNumber')} className={inputCls} /></Field>
              <Field label="Payment Terms"><input {...register('paymentTerms')} className={inputCls} placeholder="e.g., Net 15 days" /></Field>
            </div>
            <Field label="Address"><textarea {...register('address')} rows={2} className={inputCls} /></Field>
            <Field label="Notes"><textarea {...register('notes')} rows={2} className={inputCls} /></Field>
            <ModalActions onCancel={close} loading={createMutation.isLoading || updateMutation.isLoading}
              submitLabel={editing ? 'Update' : 'Add Supplier'} loadingLabel={editing ? 'Updating...' : 'Adding...'} />
          </form>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title} message={confirm.message} confirmText={confirm.confirmText} type={confirm.type}
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};
